/**
 * Registrar of record vs. upstream registrar: for a domain held through an upstream registrar
 * (WebNIC), the registrant verification and suspension that registrar runs, read back into the
 * domain, with "Gửi lại email xác minh" and a re-read. Nothing is shown for domains whose
 * connection is the registrar of record (Tino sends its own WHOIS accuracy e-mail).
 */

import { MailCheck, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import { Badge } from '../../components/Badge';
import { Callout, Section } from '../../components/blocks';
import { KeyValue } from '../../components/KeyValue';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ReasonAction } from '../operator/shared';

function tri(v: boolean | null | undefined, yes: string, no: string, unknown: string, bad: boolean) {
  if (v === null || v === undefined) return <Badge tone="neutral">{unknown}</Badge>;
  return <Badge tone={v === bad ? 'crit' : 'ok'}>{v ? yes : no}</Badge>;
}

export function UpstreamPanel({ domainId }: { domainId: string }) {
  const { t } = useTranslation('domains');
  const r = useApi((signal) => api.getDomainUpstream(domainId, { signal }), [domainId]);
  if (r.status !== 'success' || r.data.role !== 'UPSTREAM_REGISTRAR') return null;
  const st = r.data.state;
  return (
    <Section
      title={t('upstream.title')}
      description={t('upstream.hint')}
      actions={
        <div className="flex flex-wrap gap-2">
          <ReasonAction
            label={t('upstream.resend')}
            icon={MailCheck}
            title={t('upstream.resendTitle')}
            description={t('upstream.resendHint')}
            run={(reason, opts) => api.resendDomainVerification(domainId, { reason }, opts)}
            done={(x) => t('upstream.resent', { to: x.recipient_masked ?? '—' })}
            onChanged={r.reload}
          />
          <ReasonAction
            label={t('upstream.refresh')}
            icon={RefreshCw}
            variant="ghost"
            title={t('upstream.refreshTitle')}
            run={(reason, opts) => api.refreshDomainUpstream(domainId, { reason }, opts)}
            done={() => t('upstream.refreshed')}
            onChanged={r.reload}
          />
        </div>
      }
    >
      {st ? (
        <>
          {st.verified === false || st.suspended === true ? <Callout tone="warn">{t('upstream.attention')}</Callout> : null}
          <KeyValue
            columns={3}
            rows={[
              [t('upstream.verified'), tri(st.verified, t('upstream.yes'), t('upstream.notVerified'), t('upstream.unknown'), false)],
              [t('upstream.suspended'), tri(st.suspended, t('upstream.suspendedYes'), t('upstream.no'), t('upstream.notStated'), true)],
              [t('upstream.privacy'), st.whois_privacy === null ? '—' : st.whois_privacy ? t('upstream.on') : t('upstream.off')],
              [t('upstream.checkedAt'), formatDateTime(st.checked_at)],
              [t('upstream.resentAt'), st.resent_at ? `${formatDateTime(st.resent_at)} · ${st.resent_to_masked ?? ''}` : '—'],
              st.last_error ? [t('upstream.lastError'), <span key="e" className="text-xs">{st.last_error}</span>] : null,
            ]}
          />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{t('upstream.notRead')}</p>
      )}
    </Section>
  );
}
